import { useEffect, useState } from "react";
import { Home, MessageCircle } from "lucide-react";
import { PublicLayout } from "../../components/PublicLayout";
import { api } from "../../lib/api";

interface PublicLineSettings {
  line_guild_leader_url: string;
  line_official_url: string;
  line_group_checkin_url: string;
  invite_share_text: string;
  group_checkin_instruction_text: string;
}

const emptyLineSettings: PublicLineSettings = {
  line_guild_leader_url: "",
  line_official_url: "",
  line_group_checkin_url: "",
  invite_share_text: "",
  group_checkin_instruction_text: ""
};

export function GroupCheckinPage() {
  const [lineSettings, setLineSettings] = useState<PublicLineSettings>(emptyLineSettings);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api<PublicLineSettings>("/api/public/line-settings")
      .then((settings) => {
        if (!cancelled) setLineSettings(normalizeLineSettings(settings));
      })
      .catch(() => {
        if (!cancelled) setError("LINE 連結設定讀取失敗，請稍後再試。");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function openLineGroup() {
    setError("");
    const url = lineSettings.line_group_checkin_url.trim();
    if (!url) {
      setError("LINE 群連結尚未設定，請聯繫客服。");
      return;
    }
    window.location.href = url;
  }

  return (
    <PublicLayout title="LINE 群打卡" subtitle="請到指定 LINE 群完成打卡，客服確認後更新紀錄。">
      <section className="profile-panel llc-card-gradient">
        <span className="hero-kicker">LINE GROUP CHECK-IN</span>
        <h2>請到指定 LINE 群完成打卡</h2>
        <p>
          LINE 群打卡的實際動作在指定 LINE 群內完成。請依客服公告格式留言打卡；
          客服確認後，會更新你的活動紀錄。不需要在網站重複填寫或上傳證明。
        </p>
        <div className="profile-list">
          <div><dt>打卡地點</dt><dd>指定 LINE 群</dd></div>
          <div><dt>確認方式</dt><dd>客服人工確認</dd></div>
          <div><dt>網站操作</dt><dd>只提供說明與 LINE 引導</dd></div>
        </div>
      </section>

      <section className="invite-task-list">
        <article className="invite-task-card">
          <div className="invite-task-icon"><MessageCircle size={22} /></div>
          <div>
            <div className="quest-head">
              <h3>打卡流程</h3>
            </div>
            <div className="summary-list">
              <p>1. 加入指定 LINE 群。</p>
              <p>2. 依客服公告格式在群內留言打卡。</p>
              <p>3. 客服確認後，系統會更新打卡紀錄。</p>
              <p>4. 請勿在網站重複提交相同內容。</p>
            </div>
            <div className="invite-result-panel group-checkin-actions">
              <button
                className="secondary-cta cta-line-action"
                type="button"
                onClick={openLineGroup}
              >
                <MessageCircle size={16} /> 前往 LINE 群打卡
              </button>
              <a className="text-link-cta" href="/">
                <Home size={16} /> 返回大廳
              </a>
            </div>
          </div>
        </article>
      </section>

      {error ? <p className="error-box">{error}</p> : null}
    </PublicLayout>
  );
}

function normalizeLineSettings(input: unknown): PublicLineSettings {
  if (!input || typeof input !== "object" || Array.isArray(input)) return emptyLineSettings;
  const row = input as Record<string, unknown>;
  return {
    line_guild_leader_url: String(row.line_guild_leader_url ?? ""),
    line_official_url: String(row.line_official_url ?? ""),
    line_group_checkin_url: String(row.line_group_checkin_url ?? ""),
    invite_share_text: String(row.invite_share_text ?? ""),
    group_checkin_instruction_text: String(row.group_checkin_instruction_text ?? "")
  };
}
